import type { ReactNode } from 'react';
import {
  Activity,
  Bot,
  CalendarClock,
  Languages,
  Layers,
  ListChecks,
  ListTodo,
  MessagesSquare,
  Route,
  Scale,
  Settings,
  Share2,
  Table2,
  UserRound,
  type LucideIcon,
  Lightbulb,
} from 'lucide-react';
import { appPath } from '@featit/shared/addressing/paths';
import {
  type ShellTabType,
  type ToolbarKeyType,
  type ToolbarPlaceKindType,
} from '@featit/shared/shell-bridge';
import {
  namespaceActivityPath,
  namespacePath,
  NAMESPACE_DECISIONS_SEGMENT,
  NAMESPACE_TASKS_SEGMENT,
  NAMESPACE_VIEWS_SEGMENT,
  scopeItemsPath,
  WORK_AREAS_ACTIVITY_PATH,
  WORK_AREAS_DECISIONS_PATH,
  WORK_AREAS_TASKS_PATH,
} from '@/features/work-areas/workAreaOverviewUrls';
import {
  projectActivityPath,
  projectDecisionsPath,
  projectItemsPath,
  projectRoadmapPath,
  projectTasksPath,
} from '@/features/work-items/projectItemUrls';
import { SHARED_WITH_YOU_PATH } from '@/features/sharing/sharedWithYou';
import { MY_IDEAS_PATH } from '@/features/ideas/myIdeas';
import { chooseWorkAreaPath } from '@/features/work-areas/workAreaScopeRoutes';
import { WORK_AREAS_SCHEDULES_PATH } from '@/features/work-areas/workAreaOverviewUrls';
import { projectPartsPath } from '@/features/parts/partUrls';
import { projectSchedulesPath } from '@/features/schedules/scheduleUrls';
import { hasShellCapability } from '@/shell/shellDetection';
import { englishT } from '@/i18n/LocaleProvider';
import { navigation } from '@featit/shared/i18n/messages/en/navigation';

/** A key of the account rows' labels in the `navigation` catalogue area. */
type AccountLabelKeyType = `navigation.account.${keyof typeof navigation.account}`;

/** The English label, for a call with no catalogue of its own. */
function englishLabel(key: AccountLabelKeyType): string {
  return navigation.account[
    key.slice('navigation.account.'.length) as keyof typeof navigation.account
  ];
}

/**
 * I-1137 (F-0413): the one list the phone's More drawer and Settings → Mobile toolbar share. Every
 * destination a person may put in the bar (`SHELL_CUSTOMIZABLE_TAB_IDS`) is named here with what
 * draws it in the drawer, so a destination added to the customiser cannot be missing from More —
 * the guard (`moreDrawerDestinations.guard.test.ts`) fails if the two lists disagree.
 *
 * - `line`: the label of the `sidebarNavItems` line that opens it, in every scope (under All work
 *   areas and a namespace a work-area-only view opens the work-area chooser, `chooseWorkAreaPath`).
 * - `elsewhere`: drawn by something that is not a nav line, and why.
 */
export const MORE_DRAWER_DESTINATIONS: Readonly<
  Record<ShellTabType, { readonly line: string } | { readonly elsewhere: string }>
> = {
  decisions: { line: 'Decisions for me' },
  activity: { line: 'Activity' },
  items: { line: 'Work items' },
  roadmap: { line: 'Roadmap' },
  agents: { elsewhere: 'the account rows (`sidebarAccountItems`, `agents`)' },
  home: { elsewhere: 'the Home link `AppSidebar` draws above the lines' },
  recent: { elsewhere: 'the recent-view slot has no fixed destination of its own' },
  // F-0562: a slot for a destination that is not a fixed tab; each one's line is its `toolbar` key.
  custom1: { elsewhere: 'a custom slot; its destination names its own line (`toolbar`)' },
  custom2: { elsewhere: 'a custom slot; its destination names its own line (`toolbar`)' },
  custom3: { elsewhere: 'a custom slot; its destination names its own line (`toolbar`)' },
  custom4: { elsewhere: 'a custom slot; its destination names its own line (`toolbar`)' },
  more: { elsewhere: 'More is the drawer itself' },
};

export interface ISidebarNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: ReactNode;
  /**
   * F-0562 (AC-17.6a): what Settings → Mobile toolbar offers for this line — a toolbar key
   * (`ToolbarKeyType`), or the kind of a place in the selected work area (`parts`). Every line
   * names one; `moreDrawerDestinations.guard.test.ts` holds the two lists together.
   */
  toolbar: ToolbarKeyType | ToolbarPlaceKindType;
}

/**
 * The sidebar's navigation lines, and where each one points — one table, for both scopes.
 *
 * ⛔ **Every line a reader has under one work area, they have under All work areas too, or the
 * difference is stated here** (I-0560). This is the whole list because the alternative was three
 * copies of it inside `AppSidebar` — an All-work-areas branch, an admin branch and a non-admin
 * branch, the last two character-for-character identical — and *Decisions for me* was in two of the
 * three. That is how the one surface that tells a person a decision is waiting for them came to be
 * the surface that vanished when they widened their scope to see everything.
 *
 * A badge is passed in rather than built here: which lines exist and where they point is this
 * function's question, and what number sits on one is the caller's.
 */
export function sidebarNavItems({
  overviewMode,
  namespaceId = null,
  selectedWorkAreaSlug,
  decisionsBadge,
  tasksBadge,
  sharedWithYou = false,
  myIdeas = false,
  phoneMore = false,
  guest = false,
}: {
  /** The All work areas scope — the reader has widened past any single work area. */
  overviewMode: boolean;
  /**
   * One namespace's whole subtree (F-0383, §10.9) — the same aggregate line set `overviewMode`
   * gets, pointed at the namespace's own addresses instead of `/-/work-areas/*`. Never set
   * together with `overviewMode`; the caller enforces that (`ProjectStore`'s three scopes are
   * already mutually exclusive) and this function does not re-check it.
   */
  namespaceId?: string | null;
  selectedWorkAreaSlug: string | null | undefined;
  decisionsBadge: ReactNode;
  /** I-0900: same rule as `decisionsBadge` — present under both scopes, directly below it. */
  tasksBadge: ReactNode;
  /**
   * F-0271 RL-5e: something is shared with this reader as a guest. Under both scopes, like
   * *Decisions for me*: what is shared with a person does not depend on the work area on screen.
   */
  sharedWithYou?: boolean;
  /**
   * F-0461 (OF-31): the reader reported ideas. Under both scopes, like *Shared with you*: an owner's
   * ideas are his whichever work area is on screen. A reader with none sees no line.
   */
  myIdeas?: boolean;
  /**
   * Not read any more: *Parts* sits beside *Roadmap* in every work area, so anyone can start a tree
   * (concept-parts §8, F-0334). Only under one work area, because a part tree belongs to its
   * leading work area, so there is no All-work-areas twin.
   */
  hasParts?: boolean;
  /**
   * I-1137: this is the phone's More drawer, which must offer every destination Mobile toolbar offers
   * (`MORE_DRAWER_DESTINATIONS`). Under All work areas and a namespace the two that need one work
   * area (*Work items*, *Roadmap*) open the work-area chooser, never a silent redirect (I-0405). The
   * desktop sidebar keeps its aggregate set exactly as it was.
   */
  phoneMore?: boolean;
  /**
   * F-0621 (AGM-41): the reader is a guest of the selected work area — they hold only a guest role
   * or a page shared with them there. Its member lines (Activity, Work items, Decisions, Tasks,
   * Roadmap, Parts) would each open a page that refuses them, so only *Shared with you* and *My
   * ideas* are drawn; the pages shared with them are the page tree's. Read under one work area only.
   */
  guest?: boolean;
}): ISidebarNavItem[] {
  // F-0383, §10.9 NV-1: a namespace gets the identical aggregate line set All work areas gets —
  // same labels, same icons, same order — just addressed at the namespace instead.
  const aggregate = overviewMode || namespaceId !== null;

  /*
    ⛔ I-0560 (AC-1): *Decisions for me* is present under BOTH scopes. Under All it points at the
    grouped page; under one work area it points at that area's list. Gating it on a selected work
    area — which is what the code did before — is AC-1's mutation, and it is what made the line
    disappear at exactly the moment a reader asked to see everything.
  */
  const decisions: ISidebarNavItem[] = aggregate
    ? [
        {
          to: namespaceId
            ? namespacePath(namespaceId, NAMESPACE_DECISIONS_SEGMENT)
            : WORK_AREAS_DECISIONS_PATH,
          label: 'Decisions for me',
          icon: Scale,
          badge: decisionsBadge,
          toolbar: 'decisions',
        },
      ]
    : selectedWorkAreaSlug
      ? [
          {
            to: projectDecisionsPath(selectedWorkAreaSlug),
            label: 'Decisions for me',
            icon: Scale,
            badge: decisionsBadge,
            toolbar: 'decisions',
          },
        ]
      : [];

  /**
   * "Tasks for me" (I-0900), directly below Decisions: the same both-scopes rule, so widening to
   * All work areas never drops the line the way I-0560 fixed for Decisions.
   */
  const tasks: ISidebarNavItem[] = aggregate
    ? [
        {
          to: namespaceId
            ? namespacePath(namespaceId, NAMESPACE_TASKS_SEGMENT)
            : WORK_AREAS_TASKS_PATH,
          label: 'Tasks for me',
          icon: ListTodo,
          badge: tasksBadge,
          toolbar: 'tasks',
        },
      ]
    : selectedWorkAreaSlug
      ? [
          {
            to: projectTasksPath(selectedWorkAreaSlug),
            label: 'Tasks for me',
            icon: ListTodo,
            badge: tasksBadge,
            toolbar: 'tasks',
          },
        ]
      : [];

  const shared: ISidebarNavItem[] = [
    ...(myIdeas
      ? [{ to: MY_IDEAS_PATH, label: 'My ideas', icon: Lightbulb, toolbar: 'ideas' }]
      : []),
    ...(sharedWithYou || (guest && !aggregate)
      ? [{ to: SHARED_WITH_YOU_PATH, label: 'Shared with you', icon: Share2, toolbar: 'shared' }]
      : []),
  ];

  if (aggregate) {
    return [
      {
        to: namespaceId ? namespaceActivityPath(namespaceId) : WORK_AREAS_ACTIVITY_PATH,
        label: 'Activity',
        icon: Activity,
        toolbar: 'activity',
      },
      // F-0547: Work items is a list across the scope, with the item beside it.
      { to: scopeItemsPath(namespaceId), label: 'Work items', icon: ListChecks, toolbar: 'items' },
      ...decisions,
      ...tasks,
      ...shared,
      ...(phoneMore
        ? [{ to: chooseWorkAreaPath('roadmap'), label: 'Roadmap', icon: Route, toolbar: 'roadmap' }]
        : []),
      /*
        I-1336: no "Views" line under All work areas: there the work areas' views are their own
        group, drawn inline in `AppSidebar` as `<AllWorkAreasViewsList>`, whose own title still opens
        the all-work-areas Views page. A namespace keeps its line to its own Views page.
      */
      ...(namespaceId
        ? [
            {
              to: namespacePath(namespaceId, NAMESPACE_VIEWS_SEGMENT),
              label: 'Views',
              icon: Table2,
              toolbar: 'views',
            },
          ]
        : []),
    ];
  }
  // AGM-41: a guest's work area offers what is shared with them, and none of the members' lines.
  if (guest) return shared;
  /*
    ⛔ No Holds line (T-0492, TF-21). A hold waiting on the reader is a Tasks for me row and is
    answered there (Jay, 2026-09-30: "why do we have a holds view"); `/-/holds` redirects to it.
  */
  const parts: ISidebarNavItem[] =
    !aggregate && selectedWorkAreaSlug
      ? [
          {
            to: projectPartsPath(selectedWorkAreaSlug),
            // The Parts view's chrome title (in the entry), not the `parts` area: the part screens
            // register that with themselves, and the launch carries none of it.
            label: englishT('chromeTitles.parts'),
            icon: Layers,
            toolbar: 'parts',
          },
        ]
      : [];
  /*
    F-0363: no "Views" line here for one work area — the work area's views are their own group,
    drawn inline in `AppSidebar` as `<ViewNavigationList>`, the same way dashboard pages are drawn
    inline rather than through this table (F-0201's precedent). Under All work areas the views are
    a group too, by work area (I-1336, `AllWorkAreasViewsList`); only a namespace keeps a flat line.
  */
  return [
    {
      to: projectActivityPath(selectedWorkAreaSlug),
      label: 'Activity',
      icon: Activity,
      toolbar: 'activity',
    },
    {
      to: projectItemsPath(selectedWorkAreaSlug),
      label: 'Work items',
      icon: ListChecks,
      toolbar: 'items',
    },
    ...decisions,
    ...tasks,
    ...shared,
    {
      to: projectRoadmapPath(selectedWorkAreaSlug),
      label: 'Roadmap',
      icon: Route,
      toolbar: 'roadmap',
    },
    ...parts,
  ];
}

export type SidebarAccountItemIdType =
  | 'agents'
  | 'conversations'
  | 'schedules'
  | 'global-settings'
  | 'language'
  | 'profile';

/**
 * IU-13 (`docs/concept-iphone-ux.md#iu-13-the-drawers-panel-in-the-phone-form`, F-0415): which
 * chrome around the panel this reading is for, so it does not draw a destination that chrome
 * already offers.
 *
 * - `'desktop'` (default): the footer is the only copy of these lines — nothing is dropped.
 * - `'app-drawer-panel'`: the iPhone app's native More drawer, whose own fixed rows already cover
 *   Agents, (global) Settings and Profile (`ShellRootViewController.swift`,
 *   `presentMoreMenuDrawer`, read only) — dropped here regardless of what the person has put in the
 *   bar, because those rows are not the bar and never move.
 * - `'app-drawer-web'` (I-1328): the app's drawer under `drawerInWeb` — the panel is the whole menu,
 *   with no native rows above it, so it drops nothing.
 * - `'mobile-safari'`: a phone-width browser's own panel, reached from `MobileNavigation`'s *More*
 *   button or the `/-/more` page directly — there is no second, native drawer underneath it, so
 *   only `barItemIds` (what `MobileNavigation`'s bar currently shows) is dropped. `agents` is the
 *   only id the bar can ever hold (`SHELL_CUSTOMIZABLE_TAB_IDS`); the rest never reach it.
 */
export type SidebarAccountItemsContextType =
  | 'desktop'
  | 'app-drawer-panel'
  | 'app-drawer-web'
  | 'mobile-safari';

/** Settings → Language's section id (`AdminPage`), the phone menu's Language line's address. */
export const LANGUAGE_SECTION_ID = 'language';

/** The app's native drawer rows, fixed — see `SidebarAccountItemsContextType`'s own doc above. */
const APP_DRAWER_NATIVE_ITEM_IDS: ReadonlySet<SidebarAccountItemIdType> = new Set([
  'agents',
  'global-settings',
  'profile',
]);

/**
 * IU-13: which `SidebarAccountItemsContextType` a render of `AppSidebar` is in, from the two facts
 * that decide it — `Layout.tsx` calls this rather than repeating the branch at each of its three
 * `AppSidebar` call sites, so they cannot drift from one another.
 */
export function sidebarAccountItemsContextFor({
  layout,
  inShell,
}: {
  layout: 'sidebar' | 'phone';
  /** `isShellRuntime()` — the iPhone app, not a browser at phone width (mobile Safari included). */
  inShell: boolean;
}): SidebarAccountItemsContextType {
  if (layout !== 'phone') return 'desktop';
  if (!inShell) return 'mobile-safari';
  return hasShellCapability('drawerInWeb') ? 'app-drawer-web' : 'app-drawer-panel';
}

/**
 * Where the Schedules line goes (F-0424, SW-23, D-0306 c): under BOTH scopes, like *Tasks for me*. Under
 * one work area it is that work area's schedules; under All work areas and under a namespace it opens
 * the work-area chooser, as Work items and Roadmap do without a work area (a list across work areas is
 * a later child, concept-scheduled-work §18 child 11). With neither, there is nothing to open: `null`.
 * Gating the line on a selected work area is SW-23's mutation.
 */
export function schedulesPathFor({
  aggregate,
  allWorkAreas = false,
  selectedWorkAreaSlug,
}: {
  /** All work areas, or one namespace's subtree. */
  aggregate: boolean;
  /** U-0065: All work areas itself, which has a list of its own; a namespace still asks. */
  allWorkAreas?: boolean;
  selectedWorkAreaSlug: string | null | undefined;
}): string | null {
  if (allWorkAreas) return WORK_AREAS_SCHEDULES_PATH;
  if (aggregate) return chooseWorkAreaPath('schedules');
  return selectedWorkAreaSlug ? projectSchedulesPath(selectedWorkAreaSlug) : null;
}

export interface ISidebarAccountItem {
  id: SidebarAccountItemIdType;
  to: string;
  /** What the desktop sidebar calls it: a row's text, or an icon's name and tooltip. */
  label: string;
  /** What the phone menu calls it, where the sidebar's word needs its context spelled out. */
  phoneLabel: string;
  icon: LucideIcon;
  /** The phone menu's own icon, where the sidebar's would stand for two rows at once. */
  phoneIcon: LucideIcon;
  /** How the desktop sidebar draws it: a labelled line, or an icon in the footer row. */
  desktop: 'row' | 'icon';
  /** Which heading the phone menu puts it under. */
  group: 'work-area' | 'account';
  /** F-0562 (AC-17.6a): what Settings → Mobile toolbar offers for this row (see `ISidebarNavItem`). */
  toolbar: ToolbarKeyType | ToolbarPlaceKindType;
}

/**
 * The sidebar's lower part — the destinations that are not about the page list: Agents, Schedules,
 * Global settings (the one settings entry, F-0774) and the profile — as one table, for both layouts.
 *
 * ⛔ **The desktop sidebar and the phone More menu both read this** (F-0415, AC-18.3). They were
 * written out in `AppSidebar` as JSX, three of them as icons with no visible name, which is why the
 * phone menu was a row of unlabelled icons: there was no list to lay out differently, only markup.
 * A destination added here reaches both; `phoneNavigationSource.guard.test.ts` fails on a phone
 * menu that builds an address of its own.
 *
 * Notifications, Get the desktop app and Sign out are not destinations — each is a control with its
 * own behaviour — so they are not lines of this table; each has a labelled form of its own.
 */
export function sidebarAccountItems({
  schedulesPath = null,
  signedIn,
  context = 'desktop',
  barItemIds = [],
  guestOnly = false,
  guestWorkArea = null,
  t = englishLabel,
}: {
  /** The Schedules line's address (`schedulesPathFor`), or `null` when there is nothing to open. */
  schedulesPath?: string | null;
  signedIn: boolean;
  /** IU-13: see `SidebarAccountItemsContextType`. */
  context?: SidebarAccountItemsContextType;
  /**
   * `context: 'mobile-safari'` only: the ids `MobileNavigation`'s bar currently shows (from its
   * `barSlots`), so the panel can drop whichever of them it holds. Ignored for every other context.
   */
  barItemIds?: readonly SidebarAccountItemIdType[];
  /**
   * AGM-51 (6), D-0374 a (Jay): every work area the person has is a guest's (`isGuestOnlyReader`).
   * Conversations and Agents read a member's agents, machines and bridges, and a guest has none, so
   * they are left out for them rather than drawn disabled.
   */
  guestOnly?: boolean;
  /**
   * AGM-41: the selected work area is one the reader is a guest of. Its Settings and Schedules are
   * a member's. PX-18 (D-0432 b, Jay, after D-0374 a): a guest reader here meets neither, left out
   * rather than drawn disabled, whatever they hold in other work areas; a member of this work area
   * missing a permission still sees them disabled, with their reason.
   */
  guestWorkArea?: { refusedReason: (name: string) => string } | null;
  /** The reader's catalogue (`useT()`); the English one where nothing is rendered (a test, a guard). */
  t?: (key: AccountLabelKeyType) => string;
}): ISidebarAccountItem[] {
  const label = (key: keyof typeof navigation.account) => t(`navigation.account.${key}`);
  // PX-18 (D-0432 b): keyed on this work area, so a guest reader here meets neither, even if they are a
  // member elsewhere; D-0374 a's guest-only person is one such reader.
  const guestHides = guestWorkArea !== null;
  const items: ISidebarAccountItem[] = [];
  if (!guestOnly) {
    items.push(
      /*
        F-0630 (D-0359 c, Jay): Conversations is its own entry, just above Agents, so Schedules keeps
        its place directly after Agents (D-0306 c). Agents answers what each agent is doing;
        Conversations reads what was said, across every machine and harness.
      */
      {
        id: 'conversations',
        to: appPath('/conversations'),
        label: label('conversations'),
        phoneLabel: label('conversations'),
        icon: MessagesSquare,
        phoneIcon: MessagesSquare,
        desktop: 'row',
        group: 'account',
        toolbar: 'conversations',
      },
      {
        id: 'agents',
        to: appPath('/agents'),
        label: label('agents'),
        phoneLabel: label('agents'),
        icon: Bot,
        phoneIcon: Bot,
        desktop: 'row',
        group: 'account',
        toolbar: 'agents',
      },
    );
  }
  /*
    F-0424 (SW-23, Jay's D-0306 c): Schedules is operations, not planning, so it sits beside Agents in
    this lower table and not in the top block. The desktop sidebar and the phone menu both read it, so
    the line is next to Agents in both.
  */
  if (schedulesPath && !guestHides) {
    items.push({
      id: 'schedules',
      to: schedulesPath,
      label: label('schedules'),
      phoneLabel: label('schedules'),
      icon: CalendarClock,
      phoneIcon: CalendarClock,
      desktop: 'row',
      group: 'account',
      toolbar: 'schedules',
    });
  }
  /*
    F-0774 (D-0466, Jay): one settings entry. The work area's Settings is a group of Settings' rail,
    with a work-area switcher, so it has no line of its own here, on a desktop or a phone (ONE-2).
  */
  items.push({
    id: 'global-settings',
    to: appPath('/settings'),
    label: label('globalSettings'),
    phoneLabel: label('globalSettings'),
    icon: Settings,
    phoneIcon: Settings,
    // F-0774 ONE-1: a named row, never the footer's unlabelled gear: a guest's way to Language.
    desktop: 'row',
    group: 'account',
    toolbar: 'settings',
  });
  /*
    PX-29: a person's language is two presses from the navigation for everyone, guest or member. On a
    desktop the footer's Global settings opens the rail with Language in it; a phone's Global settings
    opens the list of sections, a third press, so the phone menu has a line of its own to it.
  */
  if (context !== 'desktop') {
    items.push({
      id: 'language',
      to: appPath(`/settings?section=${LANGUAGE_SECTION_ID}`),
      label: label('language'),
      phoneLabel: label('language'),
      icon: Languages,
      phoneIcon: Languages,
      desktop: 'icon',
      group: 'account',
      toolbar: 'settings',
    });
  }
  if (signedIn) {
    items.push({
      id: 'profile',
      to: appPath('/profile'),
      label: label('profile'),
      phoneLabel: label('profile'),
      icon: UserRound,
      phoneIcon: UserRound,
      desktop: 'row',
      group: 'account',
      toolbar: 'profile',
    });
  }
  if (context === 'desktop' || context === 'app-drawer-web') return items;
  const dropped: ReadonlySet<SidebarAccountItemIdType> =
    context === 'app-drawer-panel' ? APP_DRAWER_NATIVE_ITEM_IDS : new Set(barItemIds);
  return items.filter((item) => !dropped.has(item.id));
}
